import React, { useEffect, useRef, useState } from 'react';
import important from 'fela-plugin-important';
import cloneDeep from 'lodash/cloneDeep';
import {
  Block,
  CurrentTheme,
  Flex,
  SelectInput,
  Text,
  TypeVariants,
  useTheme,
  View,
} from 'vcc-ui';

const fontSizeToNumber = (fontSize: string) => {
  return Number(fontSize.replace('rem', ''));
};
function convertToPx(rem: any) {
  if (typeof rem !== 'string' || !rem.includes('rem')) {
    return rem;
  }
  return rem.replace(/(\d+(?:\.\d+)?)rem/gm, (_, p1) => {
    return `${16 * parseFloat(p1)}px`;
  });
}
const imp = important() as any;

const useForcedStyles = (styles: any) => {
  const { breakpoints } = useTheme();
  return {
    ...styles,
    ...Object.fromEntries(
      Object.keys(breakpoints).map((breakpoint) => [breakpoint, styles])
    ),
  };
};
type Breakpoint = keyof CurrentTheme['breakpoints'];
type SubStyle = 'standard' | 'emphasis' | 'inline-link';
const orderedBreakpoints: Breakpoint[] = [
  'onlyS',
  'untilM',
  'onlyM',
  'fromM',
  'untilL',
  'onlyL',
  'fromL',
  'untilXL',
  'onlyXL',
  'fromXL',
];
export const Typography: React.FC = () => {
  const theme = useTheme();
  const [subStyle, setSubStyle] = useState<SubStyle>('standard');
  const [breakpoint, setBreakpoint] = useState<Breakpoint>('onlyS');
  const variants = Object.entries(theme.typeScale).sort(
    ([, value1], [, value2]) => {
      const fontSize1 = fontSizeToNumber(value1.standard.styles.fontSize);
      const fontSize2 = fontSizeToNumber(value2.standard.styles.fontSize);
      if (fontSize1 > fontSize2) {
        return -1;
      }
      if (fontSize1 < fontSize2) {
        return 1;
      }
      return 0;
    }
  );
  return (
    <View
      height={600}
      width={750}
      direction="row"
      extend={({ theme: { color } }) => ({
        overflow: 'hidden',
        transition: 'box-shadow 150ms linear',
        border: `1px solid ${color.ornament.border}`,
        ':hover': {
          boxShadow:
            '0 0 8px 0 rgb(0 0 0 / 8%), 0 0 15px 0 rgb(0 0 0 / 2%), 0 0 20px 4px rgb(0 0 0 / 6%)',
        },
      })}
    >
      <View
        flex={1}
        height="100%"
        extend={({ theme: { color } }) => ({
          borderRight: `1px solid ${color.ornament.border}`,
        })}
      >
        <Block
          extend={({ theme: { color, baselineSubGrid, baselineGrid } }) => ({
            textAlign: 'center',
            borderBottom: `1px solid ${color.ornament.border}`,
            textTransform: 'uppercase',
            padding: `${3 * baselineSubGrid}px ${3 * baselineGrid}px`,
          })}
        >
          <Text subStyle="emphasis">Variant</Text>
        </Block>
        <View
          height="calc(100% - 48px)"
          extend={({
            theme: {
              color: { background, ornament },
            },
          }) => ({
            overflow: 'auto',
            '::-webkit-scrollbar': {
              backgroundColor: background.primary,
              width: 12,
            },
            '::-webkit-scrollbar-track': {
              backgroundColor: background.primary,
            },
            '::-webkit-scrollbar-track:hover': {
              backgroundColor: background.primary,
            },
            '::-webkit-scrollbar-thumb': {
              backgroundColor: 'transparent',
              borderRadius: 12,
              border: `5px solid ${background.primary}`,
            },
            '::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#7f7d7f',
              border: `4px solid ${background.primary}`,
            },

            '::-webkit-scrollbar-button': { display: 'none' },
            ':hover::-webkit-scrollbar-thumb': {
              backgroundColor: ornament.divider,
            },
          })}
        >
          {variants.map(([variantName, value], index) => (
            <VariantBlock
              key={variantName}
              index={index}
              name={variantName as TypeVariants}
              value={value}
              subStyle={subStyle}
              breakpoint={breakpoint}
            />
          ))}
        </View>
      </View>
      <View height="100%">
        <Block
          extend={({ theme: { color, baselineGrid, baselineSubGrid } }) => ({
            textAlign: 'center',
            borderBottom: `1px solid ${color.ornament.border}`,
            padding: `${3 * baselineSubGrid}px ${8 * baselineGrid}px`,
          })}
        >
          <Text>Customize</Text>
        </Block>
        <Block
          extend={({ theme: { baselineSubGrid } }) => ({
            padding: 3 * baselineSubGrid,
          })}
        >
          <SelectInput
            label="Sub-Style"
            value={subStyle}
            onChange={(e) => setSubStyle(e.target.value as SubStyle)}
          >
            {['standard', 'emphasis', 'inline-link'].map((subStyle) => (
              <option key={subStyle} value={subStyle}>
                {subStyle}
              </option>
            ))}
          </SelectInput>
        </Block>
        <Block
          extend={({ theme: { baselineSubGrid } }) => ({
            padding: 3 * baselineSubGrid,
          })}
        >
          <SelectInput
            label="Breakpoint"
            value={breakpoint}
            onChange={(e) => setBreakpoint(e.target.value as Breakpoint)}
          >
            {orderedBreakpoints.map((breakpoint) => (
              <option key={breakpoint} value={breakpoint}>
                {breakpoint}
              </option>
            ))}
          </SelectInput>
        </Block>
      </View>
    </View>
  );
};

const findSmallerAvailableBreakpointStyles = (
  breakpoint: Breakpoint,
  rootStyles: Record<string, any>
) => {
  const breakpointIndex = orderedBreakpoints.findIndex((b) => b === breakpoint);
  const arr = orderedBreakpoints.slice(0, breakpointIndex)?.reverse();
  return arr.reduce((acc, breakpoint) => {
    if (acc) return acc;
    acc = rootStyles[breakpoint];
    return acc;
  }, null);
};

const VariantBlock: React.FC<{
  name: TypeVariants;
  value: Record<string, any>;
  subStyle: 'standard' | 'emphasis' | 'inline-link';
  breakpoint: Breakpoint;
  index: number;
}> = ({ name, value, subStyle, breakpoint, index }) => {
  const [isOpen, setOpen] = useState(false);
  const variant = value[subStyle] || value.standard;
  const styles = variant.styles;
  const currentBreakpointStyles =
    styles[breakpoint] ||
    findSmallerAvailableBreakpointStyles(breakpoint, styles) ||
    styles;
  const forcedStyles = useForcedStyles(currentBreakpointStyles);
  const newStyles = {
    ...styles,
    ...forcedStyles,
  };
  const variantDetails = {
    Family: newStyles.fontFamily.split(',')[0],
    Weight: newStyles.fontWeight,
    Size: `${newStyles.fontSize}(${convertToPx(newStyles.fontSize)})`,
    'Line height': Math.round(newStyles.lineHeight * 100) / 100,
    Element: variant.element.toUpperCase(),
  };
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let observer: IntersectionObserver;
    let timeout: NodeJS.Timeout;
    if (index === 0) {
      observer = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              timeout = setTimeout(() => {
                setOpen(true);
              }, 100);
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -400px 0px' }
      );
      observer.observe(ref.current);
    }
    return () => {
      observer?.disconnect();
      clearTimeout(timeout);
    };
  }, [index]);
  return (
    <View
      ref={ref}
      onClick={() => setOpen((isOpen) => !isOpen)}
      extend={({ theme: { color, baselineSubGrid, baselineGrid } }) => ({
        minHeight: 30,
        overflow: 'hidden',
        padding: `${3 * baselineSubGrid}px ${2 * baselineGrid}px ${
          3 * baselineSubGrid
        }px ${3 * baselineGrid}px`,
        cursor: 'pointer',
        extend: {
          condition: isOpen,
          style: {
            background: color.background.secondary,
          },
        },
        ':hover': {
          background: color.background.secondary,
          outline: `1px solid ${color.ornament.divider}`,
        },
      })}
    >
      <Block
        extend={({ theme: { baselineSubGrid } }) => ({
          padding: `${3 * baselineSubGrid}px 0px`,
          textTransform: 'capitalize',
        })}
      >
        <Text
          variant={name}
          subStyle={subStyle}
          extend={{ ...imp(cloneDeep(newStyles)) }}
        >
          {name}
        </Text>
      </Block>
      <View
        extend={{
          justifyContent: 'space-between',
          flexDirection: 'row',
          transform: 'translateY(-1px)',
          opacity: 0,
          transitionDuration: '0.15s',
          height: 0,
          alignItems: 'center',
          width: '100%',
          extend: {
            condition: isOpen,
            style: {
              opacity: 1,
              transform: 'translateY(0)',
              height: 42,
            },
          },
        }}
      >
        {Object.entries(variantDetails).map(([title, description]) => (
          <VariantDetailsBlock
            key={title}
            title={title}
            description={description}
          />
        ))}
      </View>
    </View>
  );
};
const VariantDetailsBlock: React.FC<{
  title: string;
  description: string;
}> = ({ title, description }) => {
  return (
    <Flex>
      <Block>
        <Text variant="bates">{title}</Text>
      </Block>
      <Block extend={{ textTransform: 'capitalize' }}>
        <Text variant="bates" subStyle="emphasis">
          {description}
        </Text>
      </Block>
    </Flex>
  );
};
